import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  ProviderRequestMailStatus,
  ProviderRequestRawPayload
} from '../components/ProviderRequestAdminDetails';
import { marketplaceApi } from '../services/api';

interface InspectorRequest {
  id: string;
  projectId: string;
  projectNumber: number;
  providerType: string;
  providerId: string;
  providerName: string;
  providerEmail?: string;
  providerNumber: number;
  requesterName?: string;
  requesterEmail?: string;
  requesterPersonaType?: string;
  customerName: string;
  customerNumber: number;
  companyName: string;
  serviceType: string;
  description: string;
  timeframe: string;
  status: string;
  formPayload?: Record<string, unknown>;
  emailSentAt?: string | null;
  emailFailedAt?: string | null;
  emailFailureReason?: string | null;
  emailProviderMessageId?: string | null;
  createdAt: string;
}

type StatusFilter = 'all' | 'pending' | 'accepted' | 'declined' | 'invited';

export function AdminInspectorRequests() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<InspectorRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedPayloadId, setExpandedPayloadId] = useState<string | null>(
    null
  );

  useEffect(() => {
    marketplaceApi
      .getAdminAllRequests()
      .then((data) => {
        const inspectorRequests = data.filter(
          (r: any) => r.providerType === 'inspector'
        );
        setRequests(inspectorRequests);
      })
      .catch((err) => console.error('Failed to load inspector requests:', err))
      .finally(() => setIsLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let result = [...requests];

    if (statusFilter !== 'all') {
      result = result.filter((r) => r.status === statusFilter);
    }

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          (r.providerName || '').toLowerCase().includes(s) ||
          (r.providerEmail || '').toLowerCase().includes(s) ||
          (r.requesterName || '').toLowerCase().includes(s) ||
          (r.requesterEmail || '').toLowerCase().includes(s) ||
          (r.customerName || '').toLowerCase().includes(s) ||
          String(r.projectNumber || '').includes(s) ||
          (r.serviceType || '').toLowerCase().includes(s) ||
          (r.description || '').toLowerCase().includes(s) ||
          (r.companyName || '').toLowerCase().includes(s)
      );
    }

    return result;
  }, [requests, search, statusFilter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'declined':
        return 'danger';
      case 'invited':
        return 'primary';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '400px' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t('adminInspectorRequests.title')}
            </h5>
          </div>
          <Breadcrumb
            items={[
              { label: t('nav.home'), path: '/admin/home' },
              { label: t('admin.nav.reports') },
              { label: t('adminInspectorRequests.title') }
            ]}
          />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">
            {t('adminInspectorRequests.description')}
          </p>
        </div>
      </div>

      <Card>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <div className="d-flex align-items-center gap-3">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t('adminInspectorRequests.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '280px' }}
            />
            <select
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              style={{ width: '180px' }}
            >
              <option value="all">
                {t('adminInspectorRequests.filterAll')}
              </option>
              <option value="pending">
                {t('adminInspectorRequests.filterPending')}
              </option>
              <option value="accepted">
                {t('adminInspectorRequests.filterAccepted')}
              </option>
              <option value="declined">
                {t('adminInspectorRequests.filterDeclined')}
              </option>
              <option value="invited">
                {t('adminInspectorRequests.filterInvited')}
              </option>
            </select>
          </div>
          <span className="text-muted small">
            {t('adminInspectorRequests.total')}: {filtered.length}
          </span>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="feather-inbox fs-1 d-block mb-3"></i>
            <p className="mb-0">{t('adminInspectorRequests.noRequests')}</p>
          </div>
        ) : (
          <>
            {/* Desktop: Table view */}
            <div className="d-none d-lg-block">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>
                        {t('adminInspectorRequests.columns.providerNumber')}
                      </th>
                      <th>
                        {t('adminInspectorRequests.columns.providerName')}
                      </th>
                      <th>
                        {t('adminInspectorRequests.columnRequester')}
                      </th>
                      <th>
                        {t('adminInspectorRequests.columns.customerName')}
                      </th>
                      <th>{t('adminInspectorRequests.columns.companyName')}</th>
                      <th>
                        {t('adminInspectorRequests.columns.projectNumber')}
                      </th>
                      <th>{t('adminInspectorRequests.columns.serviceType')}</th>
                      <th>{t('adminInspectorRequests.columns.timeframe')}</th>
                      <th>{t('adminInspectorRequests.columns.status')}</th>
                      <th>{t('adminProviderRequestDetails.email.column')}</th>
                      <th>{t('adminInspectorRequests.columns.createdAt')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((req) => (
                      <tr key={req.id}>
                        <td>{req.providerNumber}</td>
                        <td>
                          <div>{req.providerName}</div>
                          {req.providerEmail && (
                            <div className="text-muted small">
                              {req.providerEmail}
                            </div>
                          )}
                        </td>
                        <td>
                          <div>
                            {req.requesterName || req.customerName || '-'}
                          </div>
                          {req.requesterEmail && (
                            <div className="text-muted small">
                              {req.requesterEmail}
                            </div>
                          )}
                          {req.requesterPersonaType && (
                            <div className="text-muted small">
                              {req.requesterPersonaType}
                            </div>
                          )}
                        </td>
                        <td>{req.customerName || '-'}</td>
                        <td>{req.companyName}</td>
                        <td>{req.projectNumber}</td>
                        <td>{req.serviceType}</td>
                        <td>{req.timeframe}</td>
                        <td>
                          <span
                            className={`badge bg-${getStatusColor(req.status)}`}
                          >
                            {t(`adminInspectorRequests.status.${req.status}`)}
                          </span>
                        </td>
                        <td>
                          <ProviderRequestMailStatus request={req} />
                        </td>
                        <td>{formatDate(req.createdAt)}</td>
                        <td>
                          <div className="d-flex flex-column gap-2">
                            <ProviderRequestRawPayload
                              request={req}
                              expanded={expandedPayloadId === req.id}
                              onToggle={() =>
                                setExpandedPayloadId(
                                  expandedPayloadId === req.id ? null : req.id
                                )
                              }
                            />
                            <button
                              className="btn btn-sm btn-primary"
                              disabled={!req.projectId}
                              onClick={() =>
                                navigate(
                                  `/admin/projects/${req.projectId}?tab=contract`
                                )
                              }
                            >
                              <i className="feather-eye me-1"></i>
                              {t('adminInspectorRequests.goToProject')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile: Card view */}
            <div className="d-lg-none d-flex flex-column gap-3">
              {filtered.map((req) => (
                <div key={req.id} className="border rounded p-3">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <span className="badge bg-light text-dark border">
                          <i className="feather-clipboard me-1"></i>#
                          {req.providerNumber} — {req.providerName}
                        </span>
                        <span className="badge bg-light text-dark border">
                          <i className="feather-user me-1"></i>
                          {req.requesterName || req.customerName || '-'}
                        </span>
                        {req.requesterEmail && (
                          <span className="badge bg-light text-dark border">
                            <i className="feather-mail me-1"></i>
                            {req.requesterEmail}
                          </span>
                        )}
                        <span className="badge bg-light text-dark border">
                          <i className="feather-home me-1"></i>
                          {req.companyName}
                        </span>
                      </div>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <span className="badge bg-light text-dark border">
                          <i className="feather-folder me-1"></i>
                          {t('adminInspectorRequests.columns.projectNumber')} #
                          {req.projectNumber}
                        </span>
                        <span className="badge bg-light text-dark border">
                          <i className="feather-tool me-1"></i>
                          {req.serviceType}
                        </span>
                        <span
                          className={`badge bg-${getStatusColor(req.status)}`}
                        >
                          {t(`adminInspectorRequests.status.${req.status}`)}
                        </span>
                      </div>
                      <ProviderRequestMailStatus request={req} />
                      {req.description && (
                        <div className="fw-semibold mb-1">
                          {req.description}
                        </div>
                      )}
                      {req.timeframe && (
                        <div className="text-muted small mb-1">
                          <i className="feather-clock me-1"></i>
                          {t('adminInspectorRequests.columns.timeframe')}:{' '}
                          {req.timeframe}
                        </div>
                      )}
                      <div className="text-muted small">
                        <i className="feather-calendar me-1"></i>
                        {t('adminInspectorRequests.columns.createdAt')}:{' '}
                        {formatDate(req.createdAt)}
                      </div>
                      <ProviderRequestRawPayload
                        request={req}
                        expanded={expandedPayloadId === req.id}
                        onToggle={() =>
                          setExpandedPayloadId(
                            expandedPayloadId === req.id ? null : req.id
                          )
                        }
                      />
                    </div>
                    <button
                      className="btn btn-sm btn-primary flex-shrink-0"
                      disabled={!req.projectId}
                      onClick={() =>
                        navigate(
                          `/admin/projects/${req.projectId}?tab=contract`
                        )
                      }
                    >
                      <i className="feather-eye me-1"></i>
                      {t('adminInspectorRequests.goToProject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </DashboardLayout>
  );
}
